import { Grid, Stack } from "@mui/material";
import {
  DATE_FORMAT,
  EMPTY_CHARACTER,
  getFormattedDateString,
} from "@pulse/shared-components";
import {
  DateRangePickerField,
  DatePickerField,
  useSpacing,
  Banner,
} from "@surya-digital/leo-reactjs-material-ui";
import { Instance } from "mobx-state-tree";
import { observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { DatePickerModel } from "../../../models/DatePickerModel";
import { DaysOfWeekModel } from "../../models/DaysOfWeekModel";
import { TimeOfDayModel } from "../../models/TimeOfDayModel";
import { getRecurringScheduleEmailValidationErrorMessage } from "../../utils/getRecurringScheduleEmailValidationErrorMessage";
import { DaysInputField } from "../inputs/DaysInputField";
import { EmailCycleTimeDropdownInputField } from "../inputs/EmailCycleTimeDropdownInputField";
import { ReadOnlyFieldContainer } from "../inputs/ReadOnlyFieldContainer";
import { useProjectDetailsStore } from "../../../store/Hooks";
import { Info } from "lucide-react";
import { getStartOfToday } from "../../../utils/DateUtils";

export type RecurringScheduleEmailFormReadOnly = Partial<{
  startDate: boolean;
  endDate: boolean;
  time: boolean;
  daysOfWeek: boolean;
}>;

interface RecurringScheduleEmailFormProps {
  startDate: Instance<typeof DatePickerModel>;
  endDate: Instance<typeof DatePickerModel>;
  time: Instance<typeof TimeOfDayModel>;
  daysOfWeek: Instance<typeof DaysOfWeekModel>;
  readonly?: RecurringScheduleEmailFormReadOnly;
}

export const RecurringScheduleEmailForm: React.FC<RecurringScheduleEmailFormProps> =
  observer(({ startDate, endDate, time, daysOfWeek, readonly = {} }) => {
    const { t } = useTranslation();
    const spacing = useSpacing();
    const projectDetails = useProjectDetailsStore();

    const currentDate = getStartOfToday();
    const disableRecurringScheduleDatePicker =
      !!projectDetails.projectEndDate &&
      projectDetails.projectEndDate < currentDate;
    const validateErrors = disableRecurringScheduleDatePicker
      ? undefined
      : getRecurringScheduleEmailValidationErrorMessage(t, {
          startDate: startDate.validationError,
          endDate: endDate.validationError,
          daysOfWeek: daysOfWeek.validationError,
        });

    return (
      <Stack gap={spacing.spaceMD}>
        <Grid container columns={2} spacing={spacing.spaceMD}>
          {!readonly.startDate && !readonly.endDate ? (
            <Grid size={2}>
              <DateRangePickerField
                value={{
                  startDate: startDate.date,
                  endDate: endDate.date,
                }}
                onChange={(value): void => {
                  startDate.setDate(value.startDate);
                  endDate.setDate(value.endDate);
                }}
                onError={(startDateError, endDateError): void => {
                  startDate.setError(startDateError);
                  startDateError && !endDate.date
                    ? endDate.resetError()
                    : endDate.setError(endDateError);
                }}
                startDateLabel={t("emailCycles.startDate")}
                endDateLabel={t("emailCycles.endDate")}
                startDatePlaceholder={t("emailCycles.datePlaceholder")}
                endDatePlaceholder={t("emailCycles.datePlaceholder")}
                format={DATE_FORMAT}
                minDate={currentDate}
                maxDate={projectDetails.projectEndDate ?? undefined}
                startDateError={!!validateErrors?.startDate}
                startDateHelperText={validateErrors?.startDate}
                endDateError={!!validateErrors?.endDate}
                endDateHelperText={validateErrors?.endDate}
                disabled={disableRecurringScheduleDatePicker}
                required
              />
            </Grid>
          ) : (
            <>
              <Grid size={1}>
                <ReadOnlyFieldContainer label={t("emailCycles.startDate")}>
                  {startDate.date
                    ? getFormattedDateString(startDate.date)
                    : EMPTY_CHARACTER}
                </ReadOnlyFieldContainer>
              </Grid>

              <Grid size={1}>
                {!readonly.endDate ? (
                  <DatePickerField
                    label={t("emailCycles.endDate")}
                    value={endDate.date}
                    onChange={endDate.setDate}
                    onError={endDate.setError}
                    format={DATE_FORMAT}
                    minDate={currentDate}
                    maxDate={projectDetails.projectEndDate ?? undefined}
                    placeholder={t("emailCycles.datePlaceholder")}
                    error={!!validateErrors?.endDate}
                    helperText={validateErrors?.endDate}
                    disabled={disableRecurringScheduleDatePicker}
                    required
                  />
                ) : (
                  <ReadOnlyFieldContainer label={t("emailCycles.endDate")}>
                    {endDate.date
                      ? getFormattedDateString(endDate.date)
                      : EMPTY_CHARACTER}
                  </ReadOnlyFieldContainer>
                )}
              </Grid>
            </>
          )}

          <Grid size={1}>
            <EmailCycleTimeDropdownInputField
              date={endDate.dateString ? endDate.date : undefined}
              time={time}
              readOnly={readonly.time ?? false}
              isRequired
            />
          </Grid>

          <Grid size={2}>
            <DaysInputField
              label={t("emailCycles.days")}
              value={[...daysOfWeek.value]}
              onChange={(_, value): void => daysOfWeek.setValue(value)}
              error={!!validateErrors?.daysOfWeek}
              helperText={validateErrors?.daysOfWeek}
              required
              readOnly={readonly.daysOfWeek}
            />
          </Grid>
        </Grid>

        {disableRecurringScheduleDatePicker && (
          <Banner
            icon={<Info />}
            message={t("emailCycles.scheduleDatePickerDisableReasonInfo")}
            severity="info"
            width="100%"
          />
        )}
      </Stack>
    );
  });
